import {Injectable} from "@angular/core";
import {BehaviorSubject, Observable, Subject, Subscription} from "rxjs";
import {AppSettingsService} from "../app-settings/app-settings.service";
import {WorkflowRoleModel, workflowRoleModelListFactory} from "./models/workflow-role.model";
import {WorkflowTargetInfoModel, workflowTargetInfoModelFactory} from "./models/workflow-target-info.model";
import {WorkflowStepStatus} from "./models/workflow-step-status.enum";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {UnsavedChangesDialogComponent} from "./unsaved-changes-dialog.component";
import {WorkflowNotificationDialogComponent} from "./notifications/workflow-notification-dialog.component";
import {exposePropertyInDebugMode} from "../misc/expose-item-in-debug-mode.function";
import {AuthDetailFlags} from "../app-settings/auth-detail.model";
import {debounceFunction} from "../misc/debounce.decorator";
import {Apollo} from "apollo-angular";
import {
  WorkflowFinishGqlModel,
  WorkflowFlagGqlModel,
  WorkflowGqlModel, WorkflowStepGqlModel,
  WorkflowStepStatusGqlModel,
} from "./models/workflow.gql-model";
import {SuccessResultGqlModel} from "../graphql/common.gql-model";

export const ALL_ROLES: WorkflowRoleModel[] = workflowRoleModelListFactory([
  /* eslint-disable no-multi-spaces */
  {key: "transition-lead",            abbreviation: "TL",   name: "Transition Lead", requiredForRegion: ["AMS", "EMEA", "APJ"]},
  {key: "account-lead",               abbreviation: "AL",   name: "Account Lead", requiredForRegion: []},
  {key: "pursuit-lead",               abbreviation: "PL",   name: "Pursuit Lead", requiredForRegion: []},
  {key: "discovery-design-lead",      abbreviation: "DDL",  name: "Discovery and Design Lead", requiredForRegion: ["AMS", "EMEA", "APJ"]},
  {key: "contract-administrator",     abbreviation: "CA",   name: "Contract Administrator", requiredForRegion: ["AMS", "EMEA", "APJ"]},
  {key: "delivery-consultant",        abbreviation: "DC",   name: "Delivery Consultant", requiredForRegion: []},
  {key: "product-support-specialist", abbreviation: "PSS",  name: "Product Support Specialist", requiredForRegion: ["AMS"]},
  {key: "delivery-admin-management",  abbreviation: "DAM",  name: "Delivery Admin Management", requiredForRegion: ["EMEA"]},
  /* eslint-enable no-multi-spaces */
]);

export enum WorkflowNavigationDirection {
  Manual = 0,
  Previous = -1,
  Next = 1,
}

/**
 * Array.reduce helper to flatten an array - give to reduce as param1 with [] as param2
 * @param a
 * @param b
 */
const flatten = (a, b) => ([...a, ...b]);

export type WorkflowNavigationInfo = {
  direction: WorkflowNavigationDirection;
  targetStep: WorkflowStepGqlModel;
};

@Injectable({providedIn: "root"})
export class WorkflowService {
  private userPrivilegeFlags: AuthDetailFlags = {};
  private workflowId: string;
  private stepName: string;
  private subStepName: string;
  private _taskLoading: boolean = false;
  private _workflowLoading: boolean = false;
  private navigationRequestedUrl: string = "";
  private lastProjectId: string;
  private workflowSubscription: Subscription;
  private lastExtraData: any;

  /* These variables are protected - don't use them except in workflow-service/workflow-step/etc */
  userIsReadOnly: boolean = true;
  lastTargetType: string;
  lastTargetId: string;
  /* End */

  /* These are used in the Workflow UI and should not be updated by other services. */
  workflowConfig: WorkflowGqlModel = new WorkflowGqlModel({});
  readableTargetName: string;
  readableTargetType: string;
  previousStep: WorkflowStepGqlModel;
  currentStep: WorkflowStepGqlModel;
  nextStep: WorkflowStepGqlModel;
  currentRoles: string[] = [];
  loading: boolean = false;
  roles: WorkflowRoleModel[];
  canContinue: boolean = false;
  /* End */

  /**
   * Update to set whether a task has unsaved data (true = unsaved data, false = ok to navigate)
   * @type {boolean}
   */
  dirty: boolean = false;

  private workflowStatusSubject: BehaviorSubject<WorkflowTargetInfoModel> =
    new BehaviorSubject<WorkflowTargetInfoModel>(undefined);

  private workflowStepStatusSubject: BehaviorSubject<WorkflowStepStatus> =
    new BehaviorSubject<WorkflowStepStatus>(undefined);

  public isMigrationActive: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private workflowNavigationSubject: Subject<WorkflowNavigationInfo> = new Subject<WorkflowNavigationInfo>();
  private workflowErrorSubject: Subject<Error> = new Subject<Error>();

  // status observables
  workflowStatus: Observable<WorkflowTargetInfoModel> = this.workflowStatusSubject.asObservable();
  workflowStepStatus: Observable<WorkflowStepStatus> = this.workflowStepStatusSubject.asObservable();

  // messaging observables
  workflowNavigation: Observable<WorkflowNavigationInfo> = this.workflowNavigationSubject.asObservable();
  workflowError: Observable<Error> = this.workflowErrorSubject.asObservable();

  /**
   * Called by WorkflowJumpComponent
   */
  reset() {
    this.previousStep = undefined;
    this.currentStep = undefined;
    this.nextStep = undefined;
    this.currentRoles = [];
    this.canContinue = false;
    this.stepName = undefined;
    this.navigationRequestedUrl = "";
  }

  /**
   * Indicates whether a task is loading.
   * @returns {boolean}
   */
  get taskLoading(): boolean {
    return this._taskLoading;
  }

  /**
   * Indicates whether a task is loading.
   * @param taskLoading
   */
  set taskLoading(taskLoading: boolean) {
    this._taskLoading = taskLoading;
    this.calculateLoading();
  }

  init(): void {
    this.workflowId = undefined;
    this.stepName = undefined;
    this.navigationRequestedUrl = "";
    this.lastTargetType = undefined;
    this.lastTargetId = undefined;
    this.lastProjectId = undefined;
    this.workflowConfig = new WorkflowGqlModel({});
    this.readableTargetName = undefined;
    this.readableTargetType = undefined;
    this.previousStep = undefined;
    this.currentStep = undefined;
    this.nextStep = undefined;
    this.currentRoles = [];
    this.roles = undefined;
    this.dirty = false;
  }

  updateLocalFields() {
    this.updateRoles();
    this.updateCanContinueFlag();

    this.readableTargetName = this.workflowConfig.readableTargetName;
    this.readableTargetType = this.workflowConfig.readableTargetType;

    this.updateWorkflowStep(this.stepName);

    this.workflowLoading = false;

    const {type, targetId, projectId, isMigrationActive} = this.workflowConfig;

    this.isMigrationActive.next(isMigrationActive);

    if ((type !== this.lastTargetType || targetId !== this.lastTargetId || projectId !== this.lastProjectId)) {
      this.lastTargetType = type;
      this.lastTargetId = targetId;
      this.lastProjectId = projectId;

      if (this.stepName) {
        this.workflowStatusSubject.next(workflowTargetInfoModelFactory({
          type,
          targetId,
          projectId,
          stepName: this.stepName,
          correctRole: this.userHasCorrectRoleForCurrentStep(),
          blocked: (!this.currentStep || this.currentStep.blocked),
          stepRoles: [...this.currentRoles],
        }));

        this.updateRoles();
        this.updateCanContinueFlag();
      }
    }

    this.updateStepStatus();
  }

  unsubscribe() {
    if (this.workflowSubscription && !this.workflowSubscription.closed) {
      this.workflowSubscription.unsubscribe();
    }
  }

  /**
   * Called by workflow router or SocketIO connection method to subscribe to a workflow.
   * There is generally no need for this method to be called manually.
   */
  updateWorkflowConfig(workflowId?: string) {
    if (workflowId && workflowId !== this.workflowId) {
      this.workflowLoading = true;
    }

    if (workflowId !== undefined) this.workflowId = workflowId;

    if (!this.workflowId || this.workflowId === "undefined" || this.workflowId === "null") return;

    this.lastTargetId = undefined;
    this.lastProjectId = undefined;
    this.lastTargetType = undefined;

    this.unsubscribe();

    this.workflowSubscription = this.subscribeToWorkflow(this.workflowId)
      .subscribe(
        (workflow) => {
          if (this.workflowConfig.id !== workflow.id) {
            this.lastExtraData = {};
          }

          this.workflowConfig = workflow;
          this.updateLocalFields();
        },
        (error) => {
          this.workflowErrorSubject.next(error);
          console.error(error);
        },
        () => {
          console.warn("closed!");
        },
      );
  }

  /**
   * Gets last extra-data state.
   */
  getExtraData(): any {
    const result = this.lastExtraData || {};
    this.lastExtraData = {};
    return result;
  }

  /**
   * Called by the step's controller during onInit to update the UI.
   * There is generally no need for this method to be called manually.
   * @param stepName
   * @param subStepName
   */
  updateWorkflowStep(stepName: string, subStepName: string = ""): void {
    const {steps = []} = this.workflowConfig;
    const lastStep = this.stepName;
    const lastSubStep = this.subStepName;

    if (!stepName && !this.stepName) {
      // happens if garbage is entered as the step name
      return;
    }

    this.stepName = stepName;
    this.subStepName = subStepName;

    const activeStepIndex = steps.findIndex(({stepName: n}) => this.stepName && n && n === this.stepName);

    this.previousStep = activeStepIndex >= 1 ? steps[activeStepIndex - 1] : undefined;
    this.currentStep = activeStepIndex >= 0 ? steps[activeStepIndex] : undefined;
    this.nextStep = (activeStepIndex >= 0 && activeStepIndex <= steps.length - 2) ?
      steps[activeStepIndex + 1] :
      undefined;

    this.currentRoles = this.currentStep ? this.currentStep.roles : ["unknown"];

    if (
      this.currentStep &&
      this.currentStep.status === WorkflowStepStatus.NotStarted &&
      this.userHasCorrectRoleForCurrentStep()
    ) {
      let allPreviousStepsComplete = true;
      for (const step of this.workflowConfig.steps) {
        if (step.stepName === this.stepName) break;
        if (step.status !== WorkflowStepStatus.Completed) {
          allPreviousStepsComplete = false;
          break;
        }
      }

      if (allPreviousStepsComplete && this.currentStep.status === WorkflowStepStatus.NotStarted) {
        this.currentStep.blocked = false;
        this.currentStep.status = WorkflowStepStatus.InProgress;
        this.setStepStatus(WorkflowStepStatus.InProgress, stepName)
          .catch((error) => {
            console.error(error);
          });
      }
    }

    if (
      this.lastTargetType &&
      this.lastTargetId &&
      this.lastProjectId &&
      (stepName !== lastStep || subStepName !== lastSubStep)
    ) {
      this.workflowStatusSubject.next(workflowTargetInfoModelFactory({
        type: this.lastTargetType,
        targetId: this.lastTargetId,
        projectId: this.lastProjectId,
        stepName,
        correctRole: this.userHasCorrectRoleForCurrentStep(),
        blocked: (!this.currentStep || this.currentStep.blocked),
        stepRoles: [...this.currentRoles],
      }));
      this.updateRoles();
      this.updateCanContinueFlag();
    }

    this.updateStepStatus();
  }

  /**
   * Update the status of a given step - or the current step if no step name is given
   * @param status to assign to step
   * @param stepName (optional) step name to use instead of current step name
   */
  async setStepStatus(status: WorkflowStepStatus, stepName?: string): Promise<void> {
    if (this.userIsReadOnly) return;

    const stepToUpdate = stepName || this.getCurrentStepName();

    if (!stepToUpdate) {
      throw new Error("No step name given or found for setStepStatus");
    }

    if (this.currentStep && this.currentStep.stepName === stepToUpdate) {
      this.currentStep.status = status;
    }

    await new Promise((resolve) => {
      const model = new WorkflowStepStatusGqlModel({workflowId: this.workflowId, stepName: stepToUpdate, status});

      this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
        .pipe(WorkflowStepStatusGqlModel.mapper)
        .subscribe(
          (result: SuccessResultGqlModel) => {
            if (!result.success) {
              console.error("An unexpected error occurred.");
            }

            resolve();
          },
          (error) => {
            console.error(error);
            resolve();
          },
        );

      setTimeout(() => resolve(), 15000); // if something happens, continue anyway
    });

    this.updateStepStatus();
  }

  private updateStepStatus() {
    const status = this.currentStep ? this.currentStep.status : undefined;

    if (this.workflowStepStatusSubject.value === status) return;
    this.workflowStepStatusSubject.next(status);
  }

  /**
   * Updates available roles and step roles (for display purposes - e.g. in step-bar)
   */
  private updateRoles(): void {
    if (!this.workflowConfig || !this.workflowConfig.steps) return;

    const newRoleList = this.workflowConfig.steps
      .map(step => step.roles)
      .reduce(flatten, []) as string[];

    this.roles = ALL_ROLES.filter(it => newRoleList.indexOf(it.key) >= 0);
  }

  private updateCanContinueFlag(): void {
    if (!this.currentStep || this.currentStep.blocked) {
      this.canContinue = false;
      return;
    }
    if (this.currentStep.status === undefined) {
      this.canContinue = false;
      return;
    }
    if (this.currentStep.status === WorkflowStepStatus.Completed) {
      this.canContinue = true;
      return;
    }
    if (this.currentStep.status === WorkflowStepStatus.NotStarted) {
      this.canContinue = false;
      return;
    }
    this.canContinue = this.userHasCorrectRoleForCurrentStep();
  }

  private getCurrentStepName(): string {
    if (this.currentStep && this.currentStep.stepName) {
      return this.currentStep.stepName;
    }
    return undefined;
  }

  public getCurrentStepStatus(): WorkflowStepStatus {
    if (this.currentStep && this.currentStep.status) {
      return this.currentStep.status;
    }
    return undefined;
  }

  /**
   * Called by UI.
   * Requests navigation but triggers a subject to start the process.
   * @param event from clicking
   * @param direction of navigation (-1, 0, or 1)
   * @param targetStep for navigation
   * @param extraData for the next step to consume
   * @returns {boolean} always false
   */
  navigate(
    event: Event,
    direction: WorkflowNavigationDirection,
    targetStep?: WorkflowStepGqlModel,
    extraData?: any,
  ): boolean {
    if (!this.currentStep && targetStep && !targetStep.blocked && targetStep.url) {
      window.location.href = targetStep.url;
      return true; // special case for overview page
    }

    if (event) {
      event.preventDefault();
    }

    if (!targetStep) return false;

    this.lastExtraData = extraData || {};

    if (!targetStep.blocked || this.canContinue) {
      this.navigationRequestedUrl = targetStep.url;
      this.workflowNavigationSubject.next({direction, targetStep});
    }

    return false;
  }

  finish(event: Event) {
    if (!this.currentStep || this.currentStep.blocked) {
      return;
    }

    const url = `#/${this.lastTargetType}?id=${this.lastTargetId}`;

    const model = new WorkflowFinishGqlModel({workflowId: this.workflowId});

    this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(WorkflowFinishGqlModel.mapper)
      .subscribe(
        (result: SuccessResultGqlModel) => {
          if (!result.success) {
            console.error("An unexpected error occurred.");
          }
        },
        (error) => {
          console.error(error);
        },
      );

    this.navigate(
      event,
      WorkflowNavigationDirection.Next,
      new WorkflowStepGqlModel({workflowId: this.workflowId, url}),
    );
  }

  /**
   * Called by the UI to show a warning about unsaved changes and continue navigation
   */
  showUnsavedChangesWarning() {
    return new Promise((resolve, reject) => {
      UnsavedChangesDialogComponent.showUnsavedChangesModal(this.dialogService)
        .subscribe(
          result => resolve(result),
          error => reject(error),
        );
    });
  }

  /**
   * Called by the system to ask whether to send notifications
   */
  showNotificationDialog(targetStep: WorkflowStepGqlModel): Promise<any> {
    return new Promise((resolve, reject) => {
      const correctRole = targetStep.roles.some(r => this.userPrivilegeFlags[r]);

      WorkflowNotificationDialogComponent.show(this.dialogService, {targetStep, correctRole})
        .subscribe(
          result => resolve(result),
          error => reject(error),
        );
    });
  }

  private userHasCorrectRoleForCurrentStep(): boolean {
    if (!this.currentRoles) return false;

    return this.currentRoles.some(r => this.userPrivilegeFlags[r]);
  }

  private get workflowLoading(): boolean {
    return this._workflowLoading;
  }

  private set workflowLoading(workflowLoading: boolean) {
    this._workflowLoading = workflowLoading;
    this.calculateLoading();
  }

  private calculateLoading() {
    this.loading = this._taskLoading || this._workflowLoading;
  }

  @debounceFunction<boolean>(500)
  private subscribeToWorkflow(workflowId): Observable<WorkflowGqlModel> {
    return this.apollo
      .subscribe({
        query: WorkflowGqlModel.query,
        variables: WorkflowGqlModel.variables(workflowId),
        fetchPolicy: "no-cache",
      })
      .pipe(WorkflowGqlModel.mapper);
  }

  setWorkflowFlag(flagName: string, flagValue: any): void {
    const model = new WorkflowFlagGqlModel({workflowId: this.workflowId, flagName, flagValue});

    this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(WorkflowFlagGqlModel.mapper)
      .subscribe(
        (result: SuccessResultGqlModel) => {
          if (!result.success) {
            console.error("An unexpected error occurred.");
          }
        },
        (error) => {
          console.error(error);
        },
      );
  }

  constructor(
    public appSettingsService: AppSettingsService,
    private dialogService: DialogService,
    private apollo: Apollo,
  ) {
    this.appSettingsService.user.subscribe((user) => {
      this.userIsReadOnly = user.readOnly;
      this.userPrivilegeFlags = user.privilegeFlags;
    });

    exposePropertyInDebugMode(
      "workflowService",
      {
        enumerable: true,
        configurable: true,
        get: (): WorkflowService => this,
      },
    );
  }
}
